import { schema } from '@app/bindx-schema'
import type { LinkEditorPanelProps, LinkTarget } from '@app/rich-editor'
import { useEntityList } from '@contember/bindx-react'
import { type FormEvent, type ReactNode, useState } from 'react'
import { useCurrentLocale } from '../fields/locale-context'
import { normalizeAnchor } from '../fields/validation'

const URL_PICKER_LIMIT = 500

/**
 * The rich-text link panel (final — not a stand-in): an external URL, or an internal `Url` row
 * picked from a list filtered to the current locale when one is in context (decision 29, same
 * rule as `LinkField`), plus `#anchor` for internal targets and "open in a new tab". Stored through
 * the editor as `ContentTestimonials → Link{type, externalTarget | internalTarget, anchor,
 * openInNewTab}`. `LinkField` itself is not reused here on purpose: it edits a `HasOneRef<Link>`,
 * while the toolbar popover works on a `LinkTarget` value the editor applies to the anchor node.
 */
export function LinkPanel({ current, onApply, onRemove }: LinkEditorPanelProps): ReactNode {
	const [kind, setKind] = useState<LinkTarget['kind']>(current?.kind ?? 'external')
	const [url, setUrl] = useState(current?.kind === 'external' ? current.href : '')
	const [openInNewTab, setOpenInNewTab] = useState(current?.openInNewTab ?? false)
	const [anchor, setAnchor] = useState(current?.kind === 'internal' ? current.anchor ?? '' : '')

	const submitExternal = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		const href = url.trim()
		if (!href) return
		onApply({ kind: 'external', href, openInNewTab })
	}

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-1">
				<KindTab selected={kind === 'external'} onClick={() => setKind('external')}>External</KindTab>
				<KindTab selected={kind === 'internal'} onClick={() => setKind('internal')}>Internal page</KindTab>
				{onRemove && <button type="button" className="btn-ghost ml-auto text-danger" onClick={onRemove}>Remove</button>}
			</div>
			<label className="flex items-center gap-2 text-sm">
				<input type="checkbox" checked={openInNewTab} onChange={e => setOpenInNewTab(e.currentTarget.checked)} />
				Open in a new tab
			</label>
			{kind === 'external'
				? (
					<form onSubmit={submitExternal} className="flex items-center gap-1.5">
						<input
							autoFocus
							type="url"
							className="h-9 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
							placeholder="https://…"
							value={url}
							onChange={e => setUrl(e.currentTarget.value)}
						/>
						<button type="submit" className="btn-orange !py-1 !px-3 text-xs" disabled={!url.trim()}>Save link</button>
					</form>
				)
				: (
					<>
						<label className="flex items-center gap-2 text-sm">
							<span className="shrink-0 text-xs text-gray">Anchor</span>
							<input
								className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm"
								placeholder="kotva (bez #)"
								value={anchor}
								onChange={e => setAnchor(e.currentTarget.value)}
								onBlur={e => setAnchor(normalizeAnchor(e.currentTarget.value) ?? '')}
							/>
						</label>
						<UrlList
							currentUrlId={current?.kind === 'internal' ? current.urlId : null}
							onPick={(urlId, url) => {
								const normalized = normalizeAnchor(anchor)
								onApply({ kind: 'internal', urlId, href: normalized ? `${url}#${normalized}` : url, anchor: normalized, openInNewTab })
							}}
						/>
					</>
				)}
		</div>
	)
}

function KindTab({ selected, onClick, children }: { selected: boolean; onClick: () => void; children: ReactNode }): ReactNode {
	return (
		<button
			type="button"
			onClick={onClick}
			className={['rounded-md px-2 py-1 text-xs font-medium', selected ? 'bg-blue-primary text-white' : 'bg-light-gray text-text'].join(' ')}
		>
			{children}
		</button>
	)
}

// `Url` rows of the current locale (all of them without a locale context), sorted by path so the
// list reads like a sitemap. `onPick` hands over the bare path; the caller appends the anchor.
function UrlList({ currentUrlId, onPick }: { currentUrlId: string | null; onPick: (urlId: string, url: string) => void }): ReactNode {
	const locale = useCurrentLocale()
	const urls = useEntityList(
		schema.Url,
		{
			filter: locale ? { locale: { id: { eq: locale.id } } } : {},
			limit: URL_PICKER_LIMIT,
			orderBy: [{ url: 'asc' }],
		},
		u => u.url().label(),
	)
	if (urls.$isLoading) return <p className="text-xs text-gray">Loading pages…</p>
	if (urls.$isError) return <p className="text-xs text-danger">Could not load the pages.</p>
	if (urls.items.length === 0) {
		return <p className="text-xs text-gray">{locale ? `There are no addresses in ${locale.label} yet.` : 'No internal pages (Url) yet.'}</p>
	}
	return (
		<ul className="max-h-48 divide-y divide-light-gray overflow-y-auto rounded-md border border-light-gray">
			{urls.items.map(item => (
				<li key={item.id}>
					<button
						type="button"
						onClick={() => onPick(item.id, item.url.value ?? '')}
						className={[
							'flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-sm hover:bg-bg-gray',
							item.id === currentUrlId ? 'bg-bg-gray font-medium' : '',
						].join(' ')}
					>
						<span className="truncate">{item.label.value || item.url.value}</span>
						<code className="shrink-0 text-xs text-gray">{item.url.value}</code>
					</button>
				</li>
			))}
		</ul>
	)
}
